import {
  V2ListProjectApprovalsForAccountApiArg,
  useV2ListProjectApprovalsForAccountQuery,
  useWhoAmIQuery,
} from "@generated";
import { PendingApproval, TimeWaitingDirection } from "../types";

export interface UsePendingApprovalsArgs {
  pageNumber: number;
  pageSize: number;
  timeWaitingDirection: TimeWaitingDirection;
}

export interface PendingApprovalsPageMeta {
  recordCount: number;
  pageCount: number;
}

// Time Waiting counts up from the approval's created-at, so the longest wait
// is the oldest created-at: the column's direction is the inverse of the sort.
const sortFor = (
  direction: TimeWaitingDirection
): V2ListProjectApprovalsForAccountApiArg["sort"] =>
  direction === "desc" ? ["created-at"] : ["-created-at"];

const formatTimeWaiting = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
  if (minutes >= 1) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  return "less than a minute";
};

const usePendingApprovals = ({
  pageNumber,
  pageSize,
  timeWaitingDirection,
}: UsePendingApprovalsArgs) => {
  const { data: me, isLoading: meLoading } = useWhoAmIQuery();
  const currentUserId = me?.data?.id?.toString();

  const {
    data,
    isLoading: approvalsLoading,
    isFetching,
    error,
    refetch,
  } = useV2ListProjectApprovalsForAccountQuery(
    {
      filter: { "pending-user-approval": currentUserId ?? "" },
      include: ["project", "project.client" as any],
      sort: sortFor(timeWaitingDirection),
      page: { size: pageSize, number: pageNumber },
    },
    { skip: !currentUserId }
  );

  const pendingApprovals: PendingApproval[] = [];

  if (data?.data) {
    const included = (data.included ?? []) as Array<{
      id?: number | string;
      type?: string;
      attributes?: Record<string, unknown>;
      relationships?: Record<string, { data?: unknown }>;
    }>;

    const clientNamesById = new Map<string, string>();
    const projectsById = new Map<
      string,
      {
        projectName: string;
        contractRevenue: number;
        contractMargin: number;
        clientId: string | null;
      }
    >();

    for (const item of included) {
      if (item.type === "clients") {
        clientNamesById.set(
          String(item.id),
          (item.attributes?.name as string) ?? ""
        );
      } else if (item.type === "projects") {
        const clientRel = item.relationships?.client?.data as
          | { id?: number | string }
          | undefined;
        projectsById.set(String(item.id), {
          projectName: (item.attributes?.["project-name"] as string) ?? "",
          contractRevenue: Number(item.attributes?.["contract-revenue"] ?? 0),
          contractMargin: Number(item.attributes?.["contract-margin"] ?? 0),
          clientId: clientRel?.id ? String(clientRel.id) : null,
        });
      }
    }

    for (const approval of data.data) {
      const projectRel = (
        approval.relationships as Record<string, { data?: unknown }>
      )?.project?.data as { id?: number | string } | undefined;
      const projectId = projectRel?.id ? String(projectRel.id) : null;
      const project = projectId ? projectsById.get(projectId) : undefined;
      if (!projectId || !project) continue;

      const requestedAt = approval.attributes?.["created-at"] ?? null;

      pendingApprovals.push({
        id: String(approval.id),
        projectId,
        projectName: project.projectName,
        clientName: project.clientId
          ? clientNamesById.get(project.clientId) ?? ""
          : "",
        contractRevenue: project.contractRevenue,
        contractMargin: project.contractMargin,
        requestedAt,
        timeWaiting: formatTimeWaiting(requestedAt),
      });
    }
  }

  const pageMeta: PendingApprovalsPageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  return {
    data: pendingApprovals,
    pageMeta,
    isLoading: approvalsLoading || meLoading,
    isFetching,
    error,
    refetch,
  };
};

export default usePendingApprovals;
